import React, { FC, useEffect, useState } from 'react';
import { Typography } from 'design_system/src';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-lg.svg';
import { ReactComponent as CircleAdd } from 'design_system/src/static/icons/add-circle.svg';
import { ReactComponent as Lock } from 'design_system/src/static/icons/lock-square.svg';
import { ReactComponent as Play } from 'design_system/src/static/icons/play-circle.svg';
// @ts-ignore
import generatePdfThumbnails from 'pdf-thumbnails-generator';

import { Grid } from '@mui/material';
import './loadDocuments.scss';
import { EvidenceDocument } from 'models/shared/types/documents';
import { momentLocal } from 'models/shared/helpers/dates';
import { resolveS3FileUrl } from 'models/upload/upload.controller';
import motoLoading from '../../../static/icons/loading_moto.gif';

const formatName = (name: string) => name.replaceAll(' ', '_');

const openDocumentUrlOnKeyActivate = (e: React.KeyboardEvent, url: string) => {
	if (e.key !== 'Enter' && e.key !== ' ') return;
	if (e.key === ' ') e.preventDefault();
	window.open(url, '_blank');
};

const mapCols: any = {
	'1': 12,
	'2': 6,
	'3': 4,
	'4': 3,
	'6': 2,
	'12': 1,
};

interface DocumentsProps<
	T extends Record<
		string,
		{
			title: string;
			subtitle?: string;
			accept: string;
		}
	>,
> {
	documents?: EvidenceDocument<keyof T>[];
	onDelete: Function;
	onAdd: Function;
	cols?: number;
	docsValues: T;
	availableDocs?: (keyof T)[];
	canDelete?: boolean;
	canAddOther?: boolean;
	otherTypeName?: string;
}

const LoadDocuments = <
	T extends Record<
		string,
		{
			title: string;
			subtitle?: string;
			accept: string;
		}
	>,
>({
	documents = [],
	onDelete,
	onAdd,
	cols = 6,
	availableDocs = [],
	docsValues,
	canDelete = true,
	canAddOther = true,
	otherTypeName,
}: DocumentsProps<T>) => {
	const [firstLoad, setFirstLoad] = useState(false);
	const [documentsView, setDocumentsView] = useState(
		<div className="flex_center_col w_100_per">
			<Typography
				weight="600"
				scale="medium"
				textColor="neutral_800"
				className="m_b_xs"
			>
				Cargando los Documentos...
			</Typography>
			<img src={motoLoading} alt="" className="h_xxxl" />
		</div>,
	);

	const genDocCard = async (file: any) => {
		const fileUrl = await resolveS3FileUrl({
			url: file.url,
			path: file.path,
		});
		let url;
		let error: any;
		if (file.mimeType.includes('image')) url = fileUrl;
		else if (file.mimeType.includes('pdf')) {
			await Promise.race([
				new Promise((res) =>
					// eslint-disable-next-line no-promise-executor-return
					setTimeout(() => {
						error = 'Timeout loading';
						res('timeout error');
					}, 5000),
				),
				generatePdfThumbnails(fileUrl, 200)
					.then((thumbnails: any) => {
						url = thumbnails[0].thumbnail;
					})
					.catch((e: any) => {
						error = e;
					}),
			]);
		} else if (file.mimeType.includes('video')) {
			return (
				<div
					id={file._id}
					className="w_100_per dso_card border_neutral_500 p_sm flex_center_col cursor_pointer flex_justify_between pos_relative document_card"
					onClick={() => window.open(fileUrl, '_blank')}
					onKeyPress={() => window.open(fileUrl, '_blank')}
					role="button"
					tabIndex={0}
				>
					{canDelete && (
						<Close
							className="dim_lg delete_file_solicitud"
							onClick={(e: any) => {
								e?.stopPropagation();
								onDelete(file);
							}}
						/>
					)}
					<Play className="dim_xl" />

					<Typography
						scale="small"
						weight="600"
						className="text_center"
						textColor="neutral_900"
					>
						Video
					</Typography>
				</div>
			);
		}
		if (error) {
			if (error.name === 'PasswordException')
				return (
					<div
						id={file._id}
						className="w_100_per dso_card border_neutral_500 p_sm flex_center_col cursor_pointer flex_justify_between pos_relative document_card"
						onClick={() => window.open(fileUrl, '_blank')}
						onKeyPress={() => window.open(fileUrl, '_blank')}
						role="button"
						tabIndex={0}
					>
						{canDelete && (
							<Close
								className="dim_lg delete_file_solicitud"
								onClick={(e: any) => {
									e?.stopPropagation();
									onDelete(file);
								}}
							/>
						)}

						<Lock className="dim_xl" />

						<Typography
							scale="small"
							weight="600"
							className="text_center"
							textColor="neutral_900"
						>
							Documento con contraseña
						</Typography>
					</div>
				);

			return (
				<div
					id={file._id}
					className="w_100_per dso_card border_neutral_500 p_sm flex_center_col cursor_pointer flex_justify_between pos_relative document_card"
					onClick={() => window.open(fileUrl, '_blank')}
					onKeyPress={() => window.open(fileUrl, '_blank')}
					role="button"
					tabIndex={0}
				>
					{canDelete && (
						<Close
							className="dim_lg delete_file_solicitud"
							onClick={(e: any) => {
								e?.stopPropagation();
								onDelete(file);
							}}
						/>
					)}

					<Close className="dim_xl" />

					<Typography
						scale="small"
						weight="600"
						className="text_center"
						textColor="neutral_900"
					>
						Fallo previsualización
					</Typography>
				</div>
			);
		}
		return (
			<div className="pos_relative" id={file._id}>
				<div
					className="w_100_per dso_card overflow_hidden cursor_pointer pos_relative document_card"
					onClick={() => window.open(fileUrl, '_blank')}
					onKeyDown={(e) => openDocumentUrlOnKeyActivate(e, fileUrl)}
					role="button"
					tabIndex={0}
					aria-label={`Abrir documento: ${file.name}`}
				>
					<img src={url} alt="" className="image center_x" />
				</div>
				{canDelete && (
					<Close
						className="dim_lg delete_file_solicitud"
						onClick={(e: any) => {
							e?.stopPropagation();
							onDelete(file);
						}}
					/>
				)}
			</div>
		);
	};
	const getDoc = async (type: keyof T, accept: string) => {
		const files = documents.filter((ele) => ele.typeEvidence === type);
		if (files.length === 0)
			return [
				<div
					id={String(type)}
					className="w_100_per dso_card border_primary_200 border_dashed p_sm flex_center_col cursor_pointer flex_justify_between pos_relative document_card"
				>
					<CircleAdd className="dim_xl text_primary_300" />

					<Typography
						scale="small"
						weight="600"
						className="text_center"
						textColor="primary_300"
					>
						Añadir Documento
					</Typography>
					<input
						type="file"
						accept={accept}
						onChange={(e: any) => onAdd(e, type)}
						className="input-file"
					/>
				</div>,
				undefined,
			];
		const card = await genDocCard(files[0]);
		return [card, files[0]];
	};
	const getDocs = async () => {
		const docs = await Promise.all(
			availableDocs.map((type: keyof T) =>
				getDoc(type, docsValues[type].accept).then(([card, doc]) => {
					return (
						<Grid item xs={mapCols[cols]} className="p_md">
							<div>
								{/* eslint-disable-next-line react/jsx-no-useless-fragment */}
								<>
									<div className="flex_center flex_gap_xxs">
										<Typography
											scale="medium"
											weight="600"
											textColor="neutral_900"
											className="m_b_sm overflow_ellipsis"
										>
											{docsValues[type].title}
										</Typography>
										<Typography
											scale="small"
											weight="400"
											textColor="neutral_900"
											className="m_b_sm"
										>
											{docsValues[type].subtitle}
										</Typography>
									</div>
									{card}
									{!!doc && (
										<Typography
											scale="small"
											weight="400"
											textColor="neutral_900"
											className="m_t_xs text_center"
										>
											Cargado:{' '}
											{momentLocal((doc as any)?.createdAt).format(
												'DD/MM/YYYY',
											)}
										</Typography>
									)}
								</>
							</div>
						</Grid>
					);
				}),
			),
		);
		const otherDocs = await Promise.all(
			documents
				.filter(
					(ele) =>
						(otherTypeName
							? otherTypeName === ele.typeEvidence
							: !availableDocs.includes(ele.typeEvidence)) &&
						ele.typeEvidence &&
						ele.mimeType,
				)
				.map((doc) =>
					genDocCard(doc).then((card) => (
						<Grid item xs={mapCols[cols]} className="p_md">
							<div>
								<div className="flex_center flex_gap_xxs">
									<Typography
										scale="medium"
										weight="600"
										textColor="neutral_900"
										className="m_b_sm overflow_ellipsis"
									>
										{formatName(doc.name)}
									</Typography>
								</div>
								{card}
								{!!doc && (
									<Typography
										scale="small"
										weight="400"
										textColor="neutral_900"
										className="m_t_xs text_center"
									>
										Cargado:{' '}
										{momentLocal((doc as any)?.createdAt).format('DD/MM/YYYY')}
									</Typography>
								)}
							</div>
						</Grid>
					)),
				),
		);
		setDocumentsView(
			<>
				{docs}
				{otherDocs ?? ''}
				{canAddOther ? (
					<Grid item xs={mapCols[cols]} className="p_md">
						<div>
							<div className="flex_center flex_gap_xxs">
								<Typography
									scale="medium"
									weight="600"
									textColor="neutral_900"
									className="m_b_sm overflow_ellipsis"
								>
									Otro
								</Typography>
							</div>
							<div
								id="add-other"
								className="w_100_per dso_card border_primary_200 border_dashed p_sm flex_center_col cursor_pointer flex_justify_between pos_relative document_card"
							>
								<CircleAdd className="dim_xl text_primary_300" />

								<Typography
									scale="small"
									weight="600"
									className="text_center"
									textColor="primary_300"
								>
									Añadir Documento
								</Typography>
								<input
									type="file"
									accept="image/png, image/jpeg, application/pdf, video/*"
									onChange={(e: any) => onAdd(e, otherTypeName ?? 'other')}
									className="input-file"
								/>
							</div>
						</div>
					</Grid>
				) : null}
			</>,
		);
	};
	useEffect(() => {
		setFirstLoad(true);
		getDocs();
	}, [documents]);
	return documentsView;
};

export default LoadDocuments;
